import { Helmet } from "react-helmet-async";

const SITE_URL = "https://beyerdigital.de";
const DEFAULT_OG = `${SITE_URL}/og-image.png`;

interface SeoProps {
  /** Ohne Suffix — der Markenname wird angehängt. */
  title: string;
  description: string;
  /** Pfad mit führendem Slash, z.B. "/cases/holzapfel". */
  path: string;
  /** Für Artikel: ändert og:type und erlaubt Datumsangaben. */
  type?: "website" | "article";
  publishedAt?: string;
  image?: string;
  /** Zusätzliches JSON-LD, z.B. Article- oder BreadcrumbList-Schema. */
  schema?: Record<string, unknown> | Record<string, unknown>[];
  /** Falls true, wird noindex, nofollow gesetzt (z.B. für 404). */
  noindex?: boolean;
}

/**
 * Setzt Meta-Tags pro Route.
 *
 * Ohne das trägt jede Seite denselben Title aus der index.html — für Google
 * sehen 16 Seiten dann aus wie Varianten derselben Seite. Beim Prerendering
 * landen die Tags im statischen HTML, weil react-helmet-async sie noch
 * während des Renderings in den <head> schreibt.
 *
 * Der Canonical ist besonders wichtig: Die Seite ist unter beyerdigital.de
 * UND www.beyerdigital.de erreichbar, beide liefern 200. Ohne Canonical
 * behandelt Google das als zwei Seiten mit identischem Inhalt und teilt die
 * Bewertung auf.
 */
export function Seo({
  title,
  description,
  path,
  type = "website",
  publishedAt,
  image = DEFAULT_OG,
  schema,
  noindex = false,
}: SeoProps) {
  const fullTitle = title.includes("beyerdigital")
    ? title
    : `${title} | beyerdigital`;
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  const schemaList = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
        }
      />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={fullTitle} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="beyerdigital" />
      {publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {schemaList.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

/** Brotkrumen-Schema — hilft Google, die Seitenstruktur zu verstehen. */
export function breadcrumbSchema(
  items: { name: string; path: string }[],
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
    })),
  };
}

export { SITE_URL };
